import 'taos';
import DesktopNavigation from '../navigation/desktop';
import MobileNavigation from '../navigation/mobile';
import {
    focusElement,
    focusableElements,
    pageScrollY,
    setPanelVisibility,
} from '../navigation/shared';

class Header {
    constructor(component) {
        this.component = component;
        this.bar = component.querySelector('[data-header-bar]');
        this.home = component.querySelector('[data-header-home]');
        this.mobileToggle = component.querySelector('[data-mobile-toggle]');
        this.languages = component.querySelector('[data-header-languages]');
        this.languageToggle = component.querySelector('[data-language-toggle]');
        this.languagePanel = component.querySelector('[data-language-panel]');
        this.languageOpen = false;
        this.searchOpen = false;
        this.media = window.matchMedia('(min-width: 1024px)');
        this.geometryFrame = null;
        this.fontScale = 1;
        this.setupPreferences();

        this.desktop = new DesktopNavigation(component, {
            onOpen: () => {
                this.closeLanguages({ immediate: true });
                this.closeModal();
            },
            onStateChange: () => this.updateState(),
        });
        this.mobile = new MobileNavigation(component, {
            onOpen: () => {
                this.desktop.closeAll({ immediate: true, moveFocus: false });
                this.closeLanguages({ immediate: true });
                this.closeModal();
            },
            onStateChange: () => this.updateState(),
        });

        this.setupLanguages();
        this.setupSearch();
        this.onModeChange();
        this.media.addEventListener('change', () => this.onModeChange());
        window.addEventListener('scroll', () => this.scheduleGeometry(), { passive: true });
        window.addEventListener('resize', () => this.scheduleGeometry(), { passive: true });
        window.visualViewport?.addEventListener('resize', () => this.scheduleGeometry(), {
            passive: true,
        });
        window.visualViewport?.addEventListener('scroll', () => this.scheduleGeometry(), {
            passive: true,
        });

        if (window.ResizeObserver) {
            this.resizeObserver = new ResizeObserver(() => this.scheduleGeometry());
            [this.bar, document.getElementById('wpadminbar')].filter(Boolean).forEach((element) => {
                this.resizeObserver.observe(element);
            });
        }

        document.fonts?.ready.then(() => this.scheduleGeometry());
        component.querySelectorAll('img').forEach((image) => {
            if (!image.complete) {
                image.addEventListener('load', () => this.scheduleGeometry(), { once: true });
            }
        });

        document.querySelectorAll('.go-top').forEach((button) => {
            button.addEventListener('click', () => {
                window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
                        ? 'instant'
                        : 'smooth',
                });
            });
        });
    }

    setupPreferences() {
        let dark = false;

        try {
            dark = sessionStorage.getItem('darkMode') === 'true';
            this.fontScale = Math.min(
                1.4,
                Math.max(0.8, parseFloat(sessionStorage.getItem('fontScale')) || 1)
            );
        } catch { }

        document.documentElement.classList.toggle('dark', dark);
        document.documentElement.style.fontSize = `${this.fontScale}em`;
        this.component.querySelectorAll('.dark-mode-toggle').forEach((button) => {
            button.classList.toggle('dark', dark);
            button.addEventListener('click', () => {
                const active = document.documentElement.classList.toggle('dark');
                this.component.querySelectorAll('.dark-mode-toggle').forEach((control) => {
                    control.classList.toggle('dark', active);
                });
                this.storePreference('darkMode', String(active));
            });
        });
        this.component.querySelectorAll('.aumentar-texto, .diminuir-texto').forEach((button) => {
            button.addEventListener('click', () => {
                const direction = button.classList.contains('aumentar-texto') ? 0.1 : -0.1;
                this.fontScale = Math.min(1.4, Math.max(0.8, this.fontScale + direction));
                document.documentElement.style.fontSize = `${this.fontScale}em`;
                this.storePreference('fontScale', this.fontScale.toFixed(2));
            });
        });
    }

    storePreference(name, value) {
        try {
            sessionStorage.setItem(name, value);
        } catch { }
    }

    setupSearch() {
        document.addEventListener('modal:beforeopen', () => {
            this.desktop.closeAll({ immediate: true, moveFocus: false });
            this.mobile.close({ immediate: true, restoreFocus: false });
            this.closeLanguages({ immediate: true });
        });
        document.addEventListener('modal:open', (event) => {
            if (event.target.getAttribute('data-modal') === 'busca') {
                this.searchOpen = true;
                this.updateState();
            }
        });
        document.addEventListener('modal:close', (event) => {
            if (event.target.getAttribute('data-modal') === 'busca') {
                this.searchOpen = false;
                this.updateState();
            }
        });
    }

    closeModal() {
        const modal = window.BASE_HELPERS?.Modal;

        if (modal?.activeModal) {
            modal.close(modal.activeModal, { restoreFocus: false, immediate: true });
        }
    }

    setupLanguages() {
        if (!this.languages || !this.languageToggle || !this.languagePanel) {
            return;
        }

        this.languageToggle.addEventListener('click', () => {
            this.languageOpen ? this.closeLanguages() : this.openLanguages();
        });
        this.languages.addEventListener('pointerenter', (event) => {
            if (event.pointerType === 'mouse') {
                this.openLanguages();
            }
        });
        this.languages.addEventListener('pointerleave', (event) => {
            if (event.pointerType === 'mouse' && !this.languages.contains(document.activeElement)) {
                this.closeLanguages();
            }
        });
        this.languages.addEventListener('focusout', (event) => {
            if (
                !this.languages.contains(event.relatedTarget) &&
                !this.languages.matches(':hover')
            ) {
                this.closeLanguages();
            }
        });
        this.languages.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && this.languageOpen) {
                event.preventDefault();
                event.stopPropagation();
                this.closeLanguages({ restoreFocus: true });
            } else if (event.target === this.languageToggle && event.key === 'ArrowDown') {
                event.preventDefault();
                this.openLanguages();
                focusElement(focusableElements(this.languagePanel)[0]);
            }
        });
        document.addEventListener('pointerdown', (event) => {
            if (this.languageOpen && !this.languages.contains(event.target)) {
                this.closeLanguages();
            }
        });
    }

    openLanguages() {
        if (!this.media.matches || !this.languagePanel || this.languageOpen) {
            return;
        }

        this.desktop.closeAll({ immediate: true });
        this.closeModal();
        this.languageOpen = true;
        this.languageToggle.setAttribute('aria-expanded', 'true');
        setPanelVisibility(this.languagePanel, true);
        this.updateState();
    }

    closeLanguages({ immediate = false, restoreFocus = false } = {}) {
        if (!this.languageOpen) {
            return;
        }

        this.languageOpen = false;
        this.languageToggle.setAttribute('aria-expanded', 'false');

        if (restoreFocus) {
            focusElement(this.languageToggle);
        }

        setPanelVisibility(this.languagePanel, false, { immediate });
        this.updateState();
    }

    onModeChange() {
        const active = document.activeElement;
        const desktopActive = this.media.matches;
        const needsFocus = desktopActive
            ? active === this.mobileToggle || this.mobile.navigation?.contains(active)
            : this.desktop.navigation?.contains(active) || this.languages?.contains(active);

        this.desktop.setEnabled(desktopActive);
        this.mobile.setEnabled(!desktopActive);
        this.closeLanguages({ immediate: true });
        this.updateGeometry();
        this.updateState();

        if (needsFocus) {
            focusElement(desktopActive ? this.home : this.mobileToggle);
        }
    }

    updateState() {
        const open = Boolean(
            this.desktop?.activeEntry || this.mobile?.isOpen || this.languageOpen || this.searchOpen
        );
        this.component.dataset.navigationOpen = String(open);
        this.scheduleGeometry();
    }

    scheduleGeometry() {
        if (this.geometryFrame !== null) {
            return;
        }

        this.geometryFrame = requestAnimationFrame(() => {
            this.geometryFrame = null;
            this.updateGeometry();
        });
    }

    updateGeometry() {
        const admin = document.getElementById('wpadminbar');
        const adminRect = admin?.getBoundingClientRect();
        const adminHeight = adminRect
            ? Math.max(0, Math.min(adminRect.height, adminRect.bottom))
            : 0;
        const barHeight = this.bar?.getBoundingClientRect().height || 70;
        const bottom = adminHeight + barHeight;
        const viewport = window.visualViewport;
        const viewportBottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
        this.component.style.top = `${adminHeight}px`;
        this.component.style.setProperty('--header-height', `${barHeight}px`);
        this.component.style.setProperty('--admin-bar-height', `${adminHeight}px`);
        this.component.style.setProperty('--header-bottom', `${bottom}px`);
        this.component.style.setProperty(
            '--navigation-height',
            `${Math.max(0, viewportBottom - bottom)}px`
        );
        this.component.dataset.scrolled = String(pageScrollY() > 150);
    }
}

document.querySelectorAll('[data-module="header"]').forEach((component) => {
    if (component.dataset.navigationInitialized !== 'true') {
        component.dataset.navigationInitialized = 'true';
        new Header(component);
    }
});

export default Header;
